/**
 * Teltonika TCP data sending protocol (ADR 0032): IMEI handshake, Codec 8 / Codec 8 Extended AVL packets and
 * Codec 12 GPRS commands. Pure: no I/O, no Node or Cloudflare API.
 *
 * Source: Teltonika wiki, "Teltonika Data Sending Protocols" and "Codec". Packet layout (TCP):
 *   preamble 0x00000000 | data length (u32, codec id .. record count 2) | codec id | records(1) | records | records(1) | CRC (u32, CRC-16/IBM in the low 16 bits)
 * Codec 8 Extended (0x8E): event IO id and every IO id are 2 bytes, counts are 2 bytes, and a variable-length
 * group (NX) follows N8. Codec 8 (0x08): 1-byte ids and counts, no NX.
 */

export const TELTONIKA_CODEC = { C8: 0x08, C8E: 0x8e, C12: 0x0c } as const;
export const CODEC12_TYPE = { command: 0x05, response: 0x06 } as const;

/** Largest TCP packet accepted (data length field). The FMC130 sends far less (about 1.3 KB by default). */
export const TELTONIKA_MAX_PACKET = 64 * 1024;

/** CRC-16/IBM (a.k.a. CRC-16/ARC): poly 0xA001 (reflected 0x8005), init 0, as specified by Teltonika. */
export function crc16ibm(bytes: Uint8Array): number {
  let crc = 0;
  for (const b of bytes) {
    crc ^= b;
    for (let i = 0; i < 8; i++) crc = crc & 1 ? (crc >>> 1) ^ 0xa001 : crc >>> 1;
  }
  return crc & 0xffff;
}

export const toHex = (b: Uint8Array): string => Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("").toUpperCase();
export function fromHex(hex: string): Uint8Array {
  const s = hex.replace(/\s+/g, "");
  if (s.length % 2 !== 0 || /[^0-9a-fA-F]/.test(s)) throw new Error("invalid hex");
  const out = new Uint8Array(s.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(s.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/* ---------------- IMEI handshake ---------------- */

/** Accept / reject byte the server answers the IMEI packet with. */
export const IMEI_ACCEPT = Uint8Array.of(0x01);
export const IMEI_REJECT = Uint8Array.of(0x00);

export type ImeiResult = { ok: true; imei: string; consumed: number } | { ok: false; need: true } | { ok: false; need: false; reason: string };

/** First packet of a connection: u16 length + ASCII IMEI (15 digits). */
export function decodeImeiPacket(buf: Uint8Array): ImeiResult {
  if (buf.length < 2) return { ok: false, need: true };
  const len = (buf[0]! << 8) | buf[1]!;
  if (len < 15 || len > 17) return { ok: false, need: false, reason: `imei length ${len}` };
  if (buf.length < 2 + len) return { ok: false, need: true };
  let imei = "";
  for (let i = 0; i < len; i++) imei += String.fromCharCode(buf[2 + i]!);
  if (!/^\d{15}$/.test(imei)) return { ok: false, need: false, reason: "imei not 15 digits" };
  return { ok: true, imei, consumed: 2 + len };
}

export function encodeImeiPacket(imei: string): Uint8Array {
  if (!/^\d{15}$/.test(imei)) throw new Error("imei must be 15 digits");
  const out = new Uint8Array(2 + imei.length);
  out[0] = 0;
  out[1] = imei.length;
  for (let i = 0; i < imei.length; i++) out[2 + i] = imei.charCodeAt(i);
  return out;
}

/** IMEI check digit (Luhn); the handshake does not require it, the allowlist is the authority. */
export function imeiLuhnOk(imei: string): boolean {
  if (!/^\d{15}$/.test(imei)) return false;
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    let d = imei.charCodeAt(i) - 48;
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return sum % 10 === 0;
}

/* ---------------- packet framing ---------------- */

/**
 * Splits the byte stream after the handshake into whole packets (preamble .. CRC). Used for AVL packets from
 * the device and for Codec 12 responses alike. A non-zero preamble or an oversized length is a protocol
 * error: the stream cannot be re-synchronised and the connection must be closed.
 */
export class TeltonikaSplitter {
  private buf = new Uint8Array(0);
  error: string | null = null;

  push(chunk: Uint8Array): Uint8Array[] {
    if (this.error) return [];
    const merged = new Uint8Array(this.buf.length + chunk.length);
    merged.set(this.buf);
    merged.set(chunk, this.buf.length);
    this.buf = merged;
    const out: Uint8Array[] = [];
    while (this.buf.length >= 8) {
      if (this.buf[0] !== 0 || this.buf[1] !== 0 || this.buf[2] !== 0 || this.buf[3] !== 0) {
        this.error = "bad preamble";
        this.buf = new Uint8Array(0);
        break;
      }
      const len = readU32(this.buf, 4);
      if (len < 3 || len > TELTONIKA_MAX_PACKET) {
        this.error = `bad length ${len}`;
        this.buf = new Uint8Array(0);
        break;
      }
      const total = 8 + len + 4;
      if (this.buf.length < total) break;
      out.push(this.buf.slice(0, total));
      this.buf = this.buf.slice(total);
    }
    return out;
  }

  get buffered(): number {
    return this.buf.length;
  }
}

const readU32 = (b: Uint8Array, o: number) => ((b[o]! << 24) >>> 0) + (b[o + 1]! << 16) + (b[o + 2]! << 8) + b[o + 3]!;
const readU16 = (b: Uint8Array, o: number) => (b[o]! << 8) | b[o + 1]!;
const readI32 = (b: Uint8Array, o: number) => (b[o]! << 24) | (b[o + 1]! << 16) | (b[o + 2]! << 8) | b[o + 3]!;

/** Checks preamble, length and CRC of one whole packet; returns the data field (codec id .. record count 2). */
export function unwrapPacket(packet: Uint8Array): { ok: true; data: Uint8Array } | { ok: false; reason: string } {
  if (packet.length < 12) return { ok: false, reason: "short packet" };
  if (readU32(packet, 0) !== 0) return { ok: false, reason: "bad preamble" };
  const len = readU32(packet, 4);
  if (packet.length !== 8 + len + 4) return { ok: false, reason: "length mismatch" };
  const data = packet.subarray(8, 8 + len);
  const crc = readU32(packet, 8 + len);
  if (crc !== crc16ibm(data)) return { ok: false, reason: "crc" };
  return { ok: true, data };
}

export function wrapPacket(data: Uint8Array): Uint8Array {
  const out = new Uint8Array(8 + data.length + 4);
  const dv = new DataView(out.buffer);
  dv.setUint32(4, data.length);
  out.set(data, 8);
  dv.setUint32(8 + data.length, crc16ibm(data));
  return out;
}

/* ---------------- AVL records ---------------- */

/** IO element value as sent: fixed groups (1/2/4/8 bytes) and the Codec 8E variable-length group. */
export type IoValue = { size: 1 | 2 | 4 | 8; raw: Uint8Array } | { size: "x"; raw: Uint8Array };

export type AvlRecord = {
  /** Device time, UTC milliseconds. */
  timestamp: number;
  /** 0 low, 1 high, 2 panic. */
  priority: number;
  /** Degrees (WGS84), from the signed 1e-7 integer. 0/0 when the device has no fix. */
  lon: number;
  lat: number;
  altitude: number;
  /** Heading, degrees from north. */
  angle: number;
  satellites: number;
  /** km/h; 0 when the GPS data is not valid. */
  speed: number;
  /** IO id that generated the record (0 = periodic). */
  eventId: number;
  io: Map<number, IoValue>;
  /** Upper-case hex of the record's own bytes (idempotency fingerprint). */
  rawHex: string;
};

export type AvlPacket = { codec: number; records: AvlRecord[] };

export type AvlDecodeResult = { ok: true; packet: AvlPacket } | { ok: false; reason: string };

/** Unsigned value of an IO element (1/2/4 bytes exactly; 8-byte values above 2^53 lose precision: use ioBig). */
export function ioUint(rec: AvlRecord, id: number): number | undefined {
  const v = rec.io.get(id);
  if (!v || v.size === "x") return undefined;
  let n = 0;
  for (const b of v.raw) n = n * 256 + b;
  return n;
}

/** Signed (two's complement) value of a 1/2/4-byte IO element. */
export function ioInt(rec: AvlRecord, id: number): number | undefined {
  const v = rec.io.get(id);
  if (!v || v.size === "x" || v.size === 8) return undefined;
  const u = ioUint(rec, id)!;
  const bits = v.size * 8;
  return u >= 2 ** (bits - 1) ? u - 2 ** bits : u;
}

export function ioBig(rec: AvlRecord, id: number): bigint | undefined {
  const v = rec.io.get(id);
  if (!v || v.size === "x") return undefined;
  let n = 0n;
  for (const b of v.raw) n = (n << 8n) | BigInt(b);
  return n;
}

/** Decodes the data field of a Codec 8 / 8E packet (use unwrapPacket first). */
export function decodeAvlData(data: Uint8Array): AvlDecodeResult {
  if (data.length < 3) return { ok: false, reason: "short data" };
  const codec = data[0]!;
  if (codec !== TELTONIKA_CODEC.C8 && codec !== TELTONIKA_CODEC.C8E) return { ok: false, reason: `codec 0x${codec.toString(16)}` };
  const ext = codec === TELTONIKA_CODEC.C8E;
  const n1 = data[1]!;
  let o = 2;
  const records: AvlRecord[] = [];
  const need = (k: number) => {
    if (o + k > data.length - 1) throw new Error("truncated record");
  };
  try {
    for (let r = 0; r < n1; r++) {
      const start = o;
      need(24);
      const ts = readU32(data, o) * 2 ** 32 + readU32(data, o + 4);
      const priority = data[o + 8]!;
      const lon = readI32(data, o + 9) / 1e7;
      const lat = readI32(data, o + 13) / 1e7;
      const altitude = (readU16(data, o + 17) << 16) >> 16;
      const angle = readU16(data, o + 19);
      const satellites = data[o + 21]!;
      const speed = readU16(data, o + 22);
      o += 24;
      const idLen = ext ? 2 : 1;
      const rd = (len: number) => (len === 2 ? readU16(data, o) : data[o]!);
      need(idLen * 2);
      const eventId = rd(idLen);
      o += idLen;
      o += idLen; // total IO count (redundant with the group counts)
      const io = new Map<number, IoValue>();
      for (const size of [1, 2, 4, 8] as const) {
        need(idLen);
        const cnt = rd(idLen);
        o += idLen;
        for (let i = 0; i < cnt; i++) {
          need(idLen + size);
          const id = rd(idLen);
          o += idLen;
          io.set(id, { size, raw: data.slice(o, o + size) });
          o += size;
        }
      }
      if (ext) {
        need(2);
        const cnt = readU16(data, o);
        o += 2;
        for (let i = 0; i < cnt; i++) {
          need(4);
          const id = readU16(data, o);
          const len = readU16(data, o + 2);
          o += 4;
          need(len);
          io.set(id, { size: "x", raw: data.slice(o, o + len) });
          o += len;
        }
      }
      records.push({ timestamp: ts, priority, lon, lat, altitude, angle, satellites, speed, eventId, io, rawHex: toHex(data.subarray(start, o)) });
    }
  } catch (e) {
    return { ok: false, reason: (e as Error).message };
  }
  if (o !== data.length - 1) return { ok: false, reason: "trailing bytes" };
  if (data[o] !== n1) return { ok: false, reason: "record count mismatch" };
  return { ok: true, packet: { codec, records } };
}

/** Decodes one whole TCP packet (preamble .. CRC) carrying AVL data. */
export function decodeAvlPacket(packet: Uint8Array): AvlDecodeResult {
  const u = unwrapPacket(packet);
  if (!u.ok) return u;
  return decodeAvlData(u.data);
}

/** Server acknowledgement of an AVL packet: the number of records accepted (u32). 0 = resend. */
export function encodeAvlAck(count: number): Uint8Array {
  const out = new Uint8Array(4);
  new DataView(out.buffer).setUint32(0, count >>> 0);
  return out;
}

/** Input for encodeAvlPacket (simulator, tests). IO values are given with their wire size. */
export type AvlRecordInput = {
  timestamp: number;
  priority?: number;
  lon?: number;
  lat?: number;
  altitude?: number;
  angle?: number;
  satellites?: number;
  speed?: number;
  eventId?: number;
  io?: { id: number; size: 1 | 2 | 4 | 8; value: number | bigint }[];
  iox?: { id: number; raw: Uint8Array }[];
};

/** Encodes records as a whole Codec 8 Extended (default) or Codec 8 TCP packet. */
export function encodeAvlPacket(records: AvlRecordInput[], codec: number = TELTONIKA_CODEC.C8E): Uint8Array {
  const ext = codec === TELTONIKA_CODEC.C8E;
  if (!ext && codec !== TELTONIKA_CODEC.C8) throw new Error("codec must be 0x08 or 0x8E");
  if (records.length < 1 || records.length > 255) throw new Error("1..255 records");
  const out: number[] = [codec, records.length];
  const push = (v: number | bigint, size: number) => {
    let x = BigInt.asUintN(size * 8, BigInt(v));
    const bytes: number[] = [];
    for (let i = 0; i < size; i++) {
      bytes.unshift(Number(x & 0xffn));
      x >>= 8n;
    }
    out.push(...bytes);
  };
  const id = (v: number) => push(v, ext ? 2 : 1);
  for (const r of records) {
    push(r.timestamp, 8);
    push(r.priority ?? 0, 1);
    push(Math.round((r.lon ?? 0) * 1e7), 4);
    push(Math.round((r.lat ?? 0) * 1e7), 4);
    push(r.altitude ?? 0, 2);
    push(r.angle ?? 0, 2);
    push(r.satellites ?? 0, 1);
    push(r.speed ?? 0, 2);
    id(r.eventId ?? 0);
    const io = r.io ?? [];
    const iox = ext ? (r.iox ?? []) : [];
    if (!ext && (r.iox?.length ?? 0) > 0) throw new Error("codec 8 has no variable-length IO");
    id(io.length + iox.length);
    for (const size of [1, 2, 4, 8] as const) {
      const g = io.filter((e) => e.size === size);
      id(g.length);
      for (const e of g) {
        id(e.id);
        push(e.value, size);
      }
    }
    if (ext) {
      push(iox.length, 2);
      for (const e of iox) {
        push(e.id, 2);
        push(e.raw.length, 2);
        out.push(...e.raw);
      }
    }
  }
  out.push(records.length);
  return wrapPacket(Uint8Array.from(out));
}

/* ---------------- Codec 12 (GPRS commands) ---------------- */

const enc = new TextEncoder();
const dec = new TextDecoder();

/** Largest command text we send (the device's GPRS command buffer is larger; this keeps commands short). */
export const CODEC12_MAX_TEXT = 512;

/** Encodes a server -> device command (type 0x05). */
export function encodeCodec12Command(text: string): Uint8Array {
  return encodeCodec12(CODEC12_TYPE.command, text);
}

/** Encodes a device -> server response (type 0x06); simulator and tests. */
export function encodeCodec12Response(text: string): Uint8Array {
  return encodeCodec12(CODEC12_TYPE.response, text);
}

function encodeCodec12(type: number, text: string): Uint8Array {
  const body = enc.encode(text);
  if (body.length < 1 || body.length > CODEC12_MAX_TEXT * 4) throw new Error("command length");
  const data = new Uint8Array(1 + 1 + 1 + 4 + body.length + 1);
  const dv = new DataView(data.buffer);
  data[0] = TELTONIKA_CODEC.C12;
  data[1] = 1;
  data[2] = type;
  dv.setUint32(3, body.length);
  data.set(body, 7);
  data[7 + body.length] = 1;
  return wrapPacket(data);
}

export type Codec12Message = { type: number; text: string };

/** Decodes a whole Codec 12 packet (either direction). */
export function decodeCodec12(packet: Uint8Array): { ok: true; msg: Codec12Message } | { ok: false; reason: string } {
  const u = unwrapPacket(packet);
  if (!u.ok) return u;
  const d = u.data;
  if (d[0] !== TELTONIKA_CODEC.C12) return { ok: false, reason: "not codec 12" };
  if (d.length < 8 || d[1] !== 1 || d[d.length - 1] !== 1) return { ok: false, reason: "quantity" };
  const type = d[2]!;
  const size = readU32(d, 3);
  if (7 + size + 1 !== d.length) return { ok: false, reason: "size mismatch" };
  return { ok: true, msg: { type, text: dec.decode(d.subarray(7, 7 + size)) } };
}

/** Codec id of a whole packet (byte 8), or null when too short. */
export const packetCodec = (packet: Uint8Array): number | null => (packet.length > 8 ? packet[8]! : null);
